import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { FileSpreadsheet, Search } from "lucide-react";
import { useMemo } from "react";

import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import {
  IStoreInvoiceExportQueued,
  IStoreInvoiceFilters,
  IStoreInvoiceParams,
} from "src/@types/store-invoice";
import { toast } from "@/components/ui/use-toast";
import {
  useExportStoreInvoicesExcelMutation,
  useGetStoreInvoiceAllowedStoresQuery,
  useGetStoreInvoicesQuery,
} from "@/services/store-invoice";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { layoutClasses } from "@/utils/css-classes";

import { StoreInvoiceFilters } from "./components/store-invoice-filters";
import { StoreInvoicePagination } from "./components/store-invoice-pagination";
import { StoreInvoiceTable } from "./components/store-invoice-table";
import { useStoreInvoiceFilterReducer } from "./reducers/useStoreInvoiceFilterReducer";
import {
  DISPLAY_DATE_FORMAT,
  EMAIL_PLACEHOLDER,
  EXCHANGE_WINDOW_WARNING,
  FilterOption,
  GLOBALLY_HANDLED_STATUS,
  ISO_DATE_LENGTH,
  MAX_PERIOD_DAYS,
  STORE_INVOICE_LABELS,
  STORE_INVOICE_MESSAGES,
} from "./types";

const toNumber = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const parsed: number = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export default function StoreInvoiceListingPage() {
  const { state, methods } = useStoreInvoiceFilterReducer();
  const { draft, applied, page, perPage } = state;

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetStoreInvoiceAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const availableStores = useMemo(() => {
    const all = (storeOptions ?? []) as FilterOption[];
    if (allowedCodes === null) return all;
    return all.filter((option: FilterOption) =>
      allowedCodes.includes(Number(option.value)),
    );
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  const storeCode: number | undefined = isStoreLocked
    ? Number(availableStores[0].value)
    : draft.storeCode;

  const queryParams: IStoreInvoiceParams | undefined = applied
    ? { ...applied, page, perPage }
    : undefined;

  const hasRequiredFilters: boolean =
    storeCode !== undefined &&
    draft.startDate !== undefined &&
    draft.endDate !== undefined;

  const { data: result, isFetching } = useGetStoreInvoicesQuery(
    queryParams as IStoreInvoiceParams,
    { skip: queryParams === undefined },
  );

  const [exportExcel, { isLoading: isExporting }] =
    useExportStoreInvoicesExcelMutation();

  const buildFilters = (): IStoreInvoiceFilters | undefined => {
    const { startDate, endDate, series } = draft;

    if (
      storeCode === undefined ||
      startDate === undefined ||
      endDate === undefined
    ) {
      toast(STORE_INVOICE_MESSAGES.MISSING_FILTERS);
      return undefined;
    }

    if (differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) > MAX_PERIOD_DAYS) {
      toast(STORE_INVOICE_MESSAGES.PERIOD_TOO_LONG);
      return undefined;
    }

    return {
      storeCode,
      startDate,
      endDate,
      invoiceStatus: toNumber(draft.invoiceStatus),
      saleStatus: toNumber(draft.saleStatus),
      documentType: toNumber(draft.documentType),
      series: series.trim() === "" ? undefined : series.trim(),
    };
  };

  const handleSearch = (): void => {
    const filters: IStoreInvoiceFilters | undefined = buildFilters();
    if (filters === undefined) return;
    methods.applyFilters(filters);
  };

  const handleExport = async (): Promise<void> => {
    const filters: IStoreInvoiceFilters | undefined = buildFilters();
    if (filters === undefined) return;

    try {
      const result = await exportExcel(filters).unwrap();
      const queued = result as IStoreInvoiceExportQueued;

      if (queued?.jobId === undefined) {
        toast(STORE_INVOICE_MESSAGES.EMPTY_EXPORT);
        return;
      }

      toast({
        description: STORE_INVOICE_MESSAGES.EXPORT_QUEUED.description.replace(
          EMAIL_PLACEHOLDER,
          queued.email,
        ),
      });
    } catch (error) {
      const status = (error as { status?: number | string })?.status;
      if (!GLOBALLY_HANDLED_STATUS.has(status as number)) {
        toast(STORE_INVOICE_MESSAGES.EXPORT_ERROR);
      }
    }
  };

  const buildExchangeWindowWarning = (): string | null => {
    const windowStart: string | null = result?.exchangeWindowStart ?? null;
    if (windowStart === null || applied === undefined) return null;
    if (applied.startDate >= windowStart.slice(0, ISO_DATE_LENGTH)) {
      return null;
    }
    return EXCHANGE_WINDOW_WARNING.replace(
      "{date}",
      format(parseISO(windowStart), DISPLAY_DATE_FORMAT),
    );
  };

  const exchangeWindowWarning: string | null = buildExchangeWindowWarning();

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={STORE_INVOICE_LABELS.pageTitle}
          description={STORE_INVOICE_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <StoreInvoiceFilters
          storeOptions={availableStores}
          storeCode={storeCode}
          isStoreLocked={isStoreLocked}
          onStoreChange={methods.setStore}
          startDate={draft.startDate}
          endDate={draft.endDate}
          onPeriodChange={methods.setPeriod}
          invoiceStatus={draft.invoiceStatus}
          saleStatus={draft.saleStatus}
          documentType={draft.documentType}
          series={draft.series}
          onInvoiceStatusChange={(value: string | undefined) =>
            methods.setOptionalField("invoiceStatus", value)
          }
          onSaleStatusChange={(value: string | undefined) =>
            methods.setOptionalField("saleStatus", value)
          }
          onDocumentTypeChange={(value: string | undefined) =>
            methods.setOptionalField("documentType", value)
          }
          onSeriesChange={methods.setSeries}
        />

        <div className="flex gap-3 mt-4">
          <Button
            onClick={handleSearch}
            disabled={!hasRequiredFilters || isFetching}
            className="h-8 bg-slate-800 hover:bg-slate-900 text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            {isFetching
              ? STORE_INVOICE_LABELS.searching
              : STORE_INVOICE_LABELS.searchButton}
          </Button>
          <Button
            onClick={handleExport}
            disabled={!hasRequiredFilters || isExporting}
            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {isExporting
              ? STORE_INVOICE_LABELS.generating
              : STORE_INVOICE_LABELS.excelButton}
          </Button>
        </div>

        {exchangeWindowWarning !== null && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-4">
            {exchangeWindowWarning}
          </p>
        )}

        <StoreInvoiceTable
          invoices={result?.data ?? []}
          isLoading={isFetching}
          emptyMessage={
            applied === undefined
              ? STORE_INVOICE_LABELS.selectFiltersHint
              : STORE_INVOICE_LABELS.emptyResult
          }
        />

        <StoreInvoicePagination
          page={page}
          pageSize={perPage}
          total={result?.total ?? 0}
          isFetching={isFetching}
          onPageChange={methods.setPage}
          onPageSizeChange={methods.setPageSize}
        />
      </div>
    </PageLayout>
  );
}
